import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { TranslationService, LanguageCode } from '../service/translation-service.service';
import { AppAuthService } from './app-auth.service';

interface UserLanguageResult {
  language: LanguageCode | null;
}

@Injectable({ providedIn: 'root' })
export class UserLanguageSyncService {
  private readonly http = inject(HttpClient);
  private readonly translation = inject(TranslationService);
  private readonly auth = inject(AppAuthService);

  /**
   * sessionStorage key recording the user-email for whom the server-wins
   * bootstrap has already run in this tab session. Without this marker, a
   * full page reload after a just-made language change would race the
   * debounced PUT and the bootstrap GET could clobber the user's choice
   * with a stale server value.
   *
   * Marker is per-user: a different user signing in on the same tab
   * (without a logout/login round-trip) re-bootstraps under their own
   * identity, since their server-side preference is independent.
   */
  private static readonly BOOTSTRAP_MARKER = 'userLanguageBootstrappedFor';

  /** Set to true while a bootstrap is applying the server's value; suppresses the echo PUT. */
  private suppressNextPut = false;

  /** Internal stream: emits when an authenticated user changes language post-bootstrap. */
  private readonly changes$ = new Subject<LanguageCode>();

  /** The user-email for whom this in-memory service instance has bootstrapped. */
  private bootstrappedUser: string | undefined;

  constructor() {
    this.auth.user$.subscribe((user) => {
      if (user) {
        // Re-bootstrap when the identity changes (covers user-switch without
        // an explicit logout, e.g. impersonation or test setup that swaps
        // the bearer token).
        if (this.bootstrappedUser !== user.email) {
          this.bootstrappedUser = user.email;
          this.bootstrap(user.email);
        }
      } else {
        // Logout: reset so next login bootstraps again, and clear the
        // sessionStorage marker so a fresh login fetches the server value.
        this.bootstrappedUser = undefined;
        try {
          sessionStorage.removeItem(UserLanguageSyncService.BOOTSTRAP_MARKER);
        } catch {
          /* sessionStorage may be unavailable in some contexts */
        }
      }
    });

    this.translation.language$.subscribe((lang) => {
      if (this.suppressNextPut) {
        this.suppressNextPut = false;
        return;
      }
      // Only sync while authenticated AND only after bootstrap completed for this session.
      if (this.bootstrappedUser !== undefined) {
        this.changes$.next(lang);
      }
    });

    this.changes$.pipe(distinctUntilChanged(), debounceTime(300)).subscribe((lang) => this.put(lang));
  }

  private bootstrap(userEmail: string): void {
    // Skip if we already bootstrapped this user in this tab session — the marker
    // survives page reloads, so a same-tab navigation after a language switch
    // keeps the local value (set in localStorage) instead of being overwritten
    // by a stale server value while the debounced PUT is still in flight. The
    // marker is per-user so a different identity on the same tab still
    // bootstraps under its own preference.
    try {
      if (sessionStorage.getItem(UserLanguageSyncService.BOOTSTRAP_MARKER) === userEmail) {
        return;
      }
    } catch {
      /* sessionStorage may be unavailable; fall through to fetch */
    }

    this.http.get<UserLanguageResult>('/api/user/language').subscribe({
      next: (res) => {
        const server = res.language;
        const local = this.translation.language;
        if (server && server !== local) {
          this.suppressNextPut = true;
          this.translation.language = server;
        } else if (!server) {
          // Server has nothing yet — push local up.
          this.put(local);
        }
        try {
          sessionStorage.setItem(UserLanguageSyncService.BOOTSTRAP_MARKER, userEmail);
        } catch {
          /* ignore */
        }
      },
      error: (err) => console.warn('UserLanguageSyncService: bootstrap GET failed', err),
    });
  }

  private put(language: LanguageCode): void {
    this.http.put<UserLanguageResult>('/api/user/language', { language }).subscribe({
      error: (err) => console.warn('UserLanguageSyncService: PUT failed', err),
    });
  }
}
